import React from 'react';
import { FormattedMessage } from 'react-intl';

// Most create-toc items are "complete" once every field they need no longer
// appears in the space's backend-computed missing-fields list.
const requiredFieldsComplete = (fields) => ({ missingItems, completionPercentage }) =>
  fields.every((field) => !missingItems.includes(field)) && 0 < completionPercentage;

export const addASpaceCreateToc = (spaceId) => [
  {
    id: 'location',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.location_header" defaultMessage="Location"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.location_description" defaultMessage="Enter your address"/>,
    link: spaceId ? `/your-space/${spaceId}/address` : "/your-space/address",
    isRequired: true,
    isComplete: requiredFieldsComplete(['city', 'country', 'street', 'zip'])
  },
  {
    id: 'setup',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.setup_header" defaultMessage="Setup"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.setup_description" defaultMessage="Add your space type, parking options, and surveillance devices"/>,
    link: `/your-space/${spaceId}/setup`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['parking_info', 'has_initial_type_tag'])
  },
  {
    id: 'space-details',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.space_details_header" defaultMessage="Space details"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.space_details_description" defaultMessage="Add a title, description, and size"/>,
    link: `/your-space/${spaceId}/space-details`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['title', 'description'])
  },
  {
    id: 'rules',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.rules_header" defaultMessage="Rules"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.rules_description" defaultMessage="Add your house rules and minimum age"/>,
    link: `/your-space/${spaceId}/rules`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['rules', 'min_age'])
  },
  {
    id: 'arrival-guide',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.arrival_guide_header" defaultMessage="Arrival guide"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.arrival_guide_description" defaultMessage="Share your wifi and arrival instructions"/>,
    link: `/your-space/${spaceId}/arrival-guide`,
    // Wifi instructions and directions are optional and aren't tracked by the
    // backend's completion checks at all, so this can't use the
    // missingItems-based check above — it reads the space's own fields
    // directly. Being optional, it must never gate `currentStep`: it's
    // neither required to complete nor allowed to block later steps.
    isRequired: false,
    isComplete: ({ space }) => !!space.wifi_instructions || !!space.getting_there
  },
  {
    id: 'photos',
    header: <FormattedMessage id="uploader.page.header" defaultMessage="Photos and video"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.photos_description" defaultMessage="Upload photos and select a cover image"/>,
    link: `/your-space/${spaceId}/photos`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['images'])
  },
  {
    id: 'availability',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.availability_header" defaultMessage="Availability"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.availability_description" defaultMessage="Set your bookable hours"/>,
    link: `/your-space/${spaceId}/availability`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['ophours'])
  },
  {
    id: 'cleaning',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.cleaning_header" defaultMessage="Cleaning"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.cleaning_description" defaultMessage="Specify your cleaning protocol for guests"/>,
    link: `/your-space/${spaceId}/cleaning-policy`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['cleaning_info'])
  },
  {
    id: 'cancellations',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.cancellations_header" defaultMessage="Cancellations"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.cancellations_description" defaultMessage="Select your cancellation policy"/>,
    link: `/your-space/${spaceId}/cancellation-policy`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['cancellation_policy'])
  },
  {
    id: 'activities',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.activities_header" defaultMessage="Activities"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.activities_description" defaultMessage="Pick the activities you’d like to host and set your price"/>,
    link: `/your-space/${spaceId}/activate-listings`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['has_listings'])
  },
  {
    id: 'policies',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.policies_header" defaultMessage="Policies"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.policies_description" defaultMessage="Learn about important host policies"/>,
    link: `/your-space/${spaceId}/policies`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['policy_accepted']),
    shouldHide: ({ profile }) => '' !== (profile.policy_accepted || '')
  },
  {
    id: 'profile',
    header: <FormattedMessage id="hosting.add_a_space_create_toc.profile_header" defaultMessage="Profile"/>,
    description: <FormattedMessage id="hosting.add_a_space_create_toc.profile_description" defaultMessage="Add a photo and contact information"/>,
    link: `/your-space/${spaceId}/profile`,
    isRequired: true,
    isComplete: requiredFieldsComplete(['first_name', 'last_name', 'phone', 'avatar_id', 'host_heard_from']),
    shouldHide: ({ profile }) => !!(profile.host_heard_from && profile.host_heard_from.length)
  }
];

export const addASpaceEditToc = (spaceId) =>[
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.location_header" defaultMessage="Location"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.location_description" defaultMessage="Address"/>,
    link: `/your-space/${spaceId}/address`,
  },
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.space_details_header" defaultMessage="Space details"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.space_details_description" defaultMessage="Title, brand name, description, and size"/>,
    link: `/your-space/${spaceId}/space-details`,
  },
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.rules_header" defaultMessage="Rules"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.rules_description" defaultMessage="House rules and minimum age"/>,
    link: `/your-space/${spaceId}/rules`,
  },
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.arrival_guide_header" defaultMessage="Arrival guide"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.arrival_guide_description" defaultMessage="Wifi and arrival instructions"/>,
    link: `/your-space/${spaceId}/arrival-guide`,
  },
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.devices_header" defaultMessage="Parking and recording devices"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.devices_description" defaultMessage="Parking options and recording device disclosures"/>,
    link: `/your-space/${spaceId}/parking`,
  },
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.cleaning_header" defaultMessage="Cleaning"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.cleaning_description" defaultMessage="Specify your cleaning protocol for guests"/>,
    link: `/your-space/${spaceId}/cleaning-policy`,
  },
  {
    header: <FormattedMessage id="hosting.add_a_space_edit_toc.cancellation_policy_header" defaultMessage="Cancellation Policy"/>,
    description: <FormattedMessage id="hosting.add_a_space_edit_toc.cancellation_policy_description" defaultMessage="Choose from Very Flexible, Flexible, Standard 30 day and Standard 90 day"/>,
    link: `/your-space/${spaceId}/cancellation-policy`,
  }
];
